import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import DevLayout from '../../components/developer/DevLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'

interface Project {
    _id: string;
    name: string;
    propertyType?: string;
    location: string;
    status: string;
    createdAt: string;
    slug?: string;
}

export default function DevDrafts() {
    const { token } = useAuth()
    const [projects, setProjects] = useState<Project[]>([])
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState<string | null>(null)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [itemToDelete, setItemToDelete] = useState<{ id: string, name: string } | null>(null)

    useEffect(() => {
        if (!token) { setLoading(false); return }
        setLoading(true);
        api.getMyProjects(token)
            .then(data => {
                if (Array.isArray(data)) {
                    // Filter for Drafts only
                    setProjects(data.filter(p => p.status === 'Draft'))
                }
            })
            .catch(err => {
                console.error("Error fetching drafts:", err);
            })
            .finally(() => setLoading(false))
    }, [token])

    const handleDeleteProject = async (id: string, name: string) => {
        setDeleting(id)
        try {
            const res = await api.deleteProject(token!, id)
            if (res.message.toLowerCase().includes('success') || res.message === 'Project deleted') {
                setProjects(p => p.filter(x => x._id !== id))
                setMsg({ type: 'success', text: 'Draft deleted successfully.' })
            } else {
                setMsg({ type: 'error', text: res.message || 'Delete failed.' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'System error during deletion.' })
        }
        setDeleting(null)
        setItemToDelete(null)
    }

    return (
        <DevLayout>
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-5 gap-3">
                <div>
                    <h3 className="fw-black text-[#0F2A5F] mb-1 uppercase italic tracking-tight">Venture Drafts</h3>
                    <p className="text-[#0F2A5F]/60 mb-0 small uppercase tracking-widest font-bold">Resume editing your unfinished listing protocols.</p>
                </div>
                <div>
                    <Link to="/developer-add-project" className="btn d-inline-flex align-items-center gap-2 shadow-lg px-4 py-3 rounded-4 border-0" style={{ backgroundColor: '#0F2A5F', color: '#D4A017' }}>
                        <i className="fa-solid fa-plus text-[10px]"></i> <span className="fw-black uppercase tracking-widest text-[10px]">Start New Project</span>
                    </Link>
                </div>
            </div>

            {msg && (
                <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'} border-0 shadow-sm mb-4`}>
                    {msg.text}
                </div>
            )}

            <div className="card border-0 shadow-xl rounded-[2rem] overflow-hidden">
                <div className="px-5 py-4 bg-white border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-black text-[#0F2A5F] uppercase italic">
                        Unfinished Drafts ({projects.length})
                    </h5>
                    <div className="h-1 bg-[#D4A017] rounded-pill w-16"></div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: '#0F2A5F' }} role="status"></div>
                        <p className="mt-3 text-[#0F2A5F] fw-black uppercase tracking-widest text-[10px]">Retrieving Draft Repositories...</p>
                    </div>
                ) : projects.length === 0 ? (
                    <div className="text-center py-5 bg-white">
                        <i className="fa-solid fa-file-signature fa-4x mb-4 opacity-50" style={{ color: '#0F2A5F' }}></i>
                        <h4 className="fw-black text-[#0F2A5F] uppercase italic">No Drafts Found</h4>
                        <p className="text-[#0F2A5F]/60 mb-4 font-bold">All of your development profiles are currently broadcasted or active.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover align-middle m-0">
                            <thead className="bg-[#0F2A5F]/5 py-4">
                                <tr>
                                    <th className="ps-5 border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Draft Name</th>
                                    <th className="border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Location Matrix</th>
                                    <th className="border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Last Modified</th>
                                    <th className="pe-5 border-0 text-end text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Operations</th>
                                </tr>
                            </thead>
                            <tbody>
                                {projects.map(p => (
                                    <tr key={p._id} className="border-bottom border-gray-50">
                                        <td className="ps-5 py-4">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="flex-shrink-0">
                                                    <div className="bg-[#0F2A5F]/5 rounded-4 d-flex align-items-center justify-content-center border" style={{ width: 70, height: 50 }}>
                                                        <i className="fa-solid fa-file-pen text-[#0F2A5F] opacity-30"></i>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="fw-black text-[#0F2A5F] uppercase italic">{p.name || 'Untitled Draft'}</div>
                                                    <small className="text-[#D4A017] fw-bold uppercase tracking-widest text-[9px]">{p.propertyType || 'Project'}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="text-[#0F2A5F]/70 fw-bold small uppercase tracking-tight">
                                                <i className="fa-solid fa-location-crosshairs me-2 opacity-50"></i>{p.location || 'Not Specified'}
                                            </span>
                                        </td>
                                        <td><small className="text-[#0F2A5F]/40 fw-black text-[10px]">{new Date(p.createdAt).toLocaleDateString('en-IN')}</small></td>
                                        <td className="pe-5 text-end">
                                            <div className="d-flex justify-content-end gap-2">
                                                <Link to={`/developer-edit-project/${p._id}`} className="px-4 py-2 rounded-3 bg-gray-50 text-[#D4A017] d-inline-flex align-items-center gap-2 hover:bg-[#D4A017] hover:text-white transition-all shadow-sm border border-gray-100 no-underline" title="Resume Editing">
                                                    <i className="fa-solid fa-pencil text-xs"></i>
                                                    <span className="fw-black uppercase tracking-widest text-[9px]">Resume</span>
                                                </Link>
                                                <button className="w-10 h-10 rounded-3 bg-gray-50 text-danger flex items-center justify-center hover:bg-danger hover:text-white transition-all shadow-sm border border-gray-100"
                                                    disabled={deleting === p._id}
                                                    onClick={() => setItemToDelete({ id: p._id, name: p.name })}
                                                    title="Delete Draft"
                                                >
                                                    {deleting === p._id
                                                        ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }}></span>
                                                        : <i className="fa-solid fa-trash-can text-xs"></i>
                                                    }
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {itemToDelete && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#0F2A5F]/40 backdrop-blur-sm">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-md overflow-hidden border border-[#0F2A5F]/10 shadow-2xl animate__animated animate__zoomIn animate__faster">
                        <div className="p-8 text-center">
                            <div className="w-20 h-20 bg-danger/10 text-danger rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className="fa-solid fa-trash-can text-3xl"></i>
                            </div>
                            <h3 className="text-xl font-black text-[#0F2A5F] uppercase italic mb-3">Delete Draft</h3>
                            <p className="text-[#0F2A5F]/60 font-medium mb-8">
                                Are you sure you want to permanently delete the draft <span className="font-bold text-[#0F2A5F]">"{itemToDelete.name || 'Untitled Draft'}"</span>?
                                This action is permanent and cannot be undone.
                            </p>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <button 
                                    onClick={() => setItemToDelete(null)}
                                    className="py-4 bg-gray-50 text-[#0F2A5F] rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-100 transition-all"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={() => handleDeleteProject(itemToDelete.id, itemToDelete.name)}
                                    disabled={deleting !== null}
                                    className="py-4 bg-danger text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-danger/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                                >
                                    {deleting ? (
                                        <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }}></span>
                                    ) : "Delete Forever"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DevLayout>
    )
}
